import React, { ReactElement, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { localeStore } from 'src/mobx/localesStore';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import CustomModalComponent from 'src/view/components/Controls/Modal/Modal';
import ModalBackground from 'src/view/components/Controls/Modal/ModalBackground/ModalBackground';
import Text, { TEXT } from 'src/view/components/Text/Text';
import { modalStore } from '~/mobx/modalStore';
import './style.scss';
import ModalHeader from '~/view/components/Controls/Modal/ModalHeader/ModalHeader';
import ModalFooter from '~/view/components/Controls/Modal/ModalFooter/ModalFooter';
import itemsStore from '~/view/screens/MenuManagementScreen/screens/ItemsScreen/store/itemsStore';
import Picker from '~/view/components/Controls/Pickers/Picker/Picker';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';
import { nutrientsStore } from '~/view/containers/Modals/NutrientsModal/NutrientsStore';
import { nutrientFieldsConfig } from '~/view/containers/Modals/NutrientsModal/NutrientFieldsConfig';
import { MEASURE_NUTRIENT } from '~/constants/main';
import Input from '~/view/components/Controls/InputComponent/Input';
import { appStore } from '~/mobx/appStore';
import IntegrationManager from '~/view/components/IntegrationManager/IntegrationManager';
import { RESTRICTION_ACCESS } from '~/constants/integrations';
import NutrientFieldWrapper from '~/view/containers/Modals/NutrientsModal/NutrientFieldWrapper';
import { IProductNutrient } from '~/types/product';

export const NutrientsModal = observer((): ReactElement | null => {
  useEffect(() => {
    nutrientsStore.setTempNutrients(modalStore.nutrientsModal?.itemNutrients ?? null);
  }, []);

  const selectedMeasure = useMemo((): IPickerOption => {
    let measure = MEASURE_NUTRIENT.GR;

    if (nutrientsStore.tempNutrientValues.serving_unit) {
      measure = nutrientsStore.tempNutrientValues.serving_unit;
    }
    return {
      label: measure,
      value: measure,
    } as IPickerOption;
  }, [nutrientsStore.tempNutrientValues]);
  const updateNutrient = (fieldName: string, value?: string) => {
    nutrientsStore.setTempNutrientValue(fieldName, value ?? null);
  };

  const applyNutrients = () => {
    if (nutrientsStore.tempNutrientValues.serving_size === null || nutrientsStore.tempNutrientValues.serving_size === 0) {
      nutrientsStore.validateServingSize(Number(nutrientsStore.tempNutrientValues.serving_size));
    } else {
      itemsStore.applyNutrients(nutrientsStore.nutrientsList);
      modalStore.closeNutrientsModal();
    }
  };

  const measurementsOptions = (): IPickerOption[] => {
    return Object.values(MEASURE_NUTRIENT).map((measure) => {
      return {
        label: measure,
        value: measure,
      } as IPickerOption;
    });
  };

  return (
    <CustomModalComponent className="nutrient-modal" isOpen onRequestClose={modalStore.closeNutrientsModal} withCloseIcon>
      <ModalBackground>
        <ModalHeader title={localeStore.t('ap_item_nutrients_popup_hdr')} wrapperClassName="nutrient-hdr" titleClassName="mb-1">
          <div className="family-medium text-size-14 color-gray2">{localeStore.t('ap_item_nutrients_popup_values_block_infotext')}</div>
        </ModalHeader>
        <div className="pos-relative d-flex flex-1 flex-column px-8">
          <div className="row mt-5">
            <IntegrationManager
              setting={appStore.settings?.is_pos ? RESTRICTION_ACCESS.DISABLED : RESTRICTION_ACCESS.ALLOWED}
              wrapperClassName="col-3 my-2"
              withTooltip
            >
              <Text required type={TEXT.BLOCK_HEADER} text="ap_item_nutrients_popup_serving_size_field" />
              <Input
                className="nutrient-input py-0 mt-2"
                value={String(nutrientsStore.tempNutrientValues.serving_size ?? '')}
                type="floatNumber"
                name="text"
                onChangeValue={(val) => updateNutrient('serving_size', val)}
                placeholder={localeStore.t('Value')}
                validation={{
                  isValid: nutrientsStore.isValid,
                  errorText: undefined,
                }}
                disabled={nutrientsStore.tempNutrientValues.serving_unit === MEASURE_NUTRIENT.PORTION}
              />
            </IntegrationManager>
            <IntegrationManager
              setting={appStore.settings?.is_pos ? RESTRICTION_ACCESS.DISABLED : RESTRICTION_ACCESS.ALLOWED}
              wrapperClassName="col-3 my-2"
              withTooltip
            >
              <Text type={TEXT.BLOCK_HEADER} text="ap_item_nutrients_popup_serving_unit_dropdown" />
              <Picker
                className="nutrient-input py-0 mt-2"
                selectedOptions={[selectedMeasure]}
                options={measurementsOptions()}
                onSelect={(val) => {
                  if (val[0]?.value) {
                    updateNutrient('serving_unit', val[0].value);
                  }
                }}
                numberVisibleOptionsInDropdown={3}
                hideResetIcon={true}
              />
            </IntegrationManager>
          </div>
          <div className="family-medium text-size-14 py-5 text-uppercase color-gray2">
            {localeStore.t('ap_item_nutrients_popup_values_block_hdr')}
          </div>
          <div className="row">
            {nutrientFieldsConfig.map(({ field, label, unit, tooltip }) => (
              <NutrientFieldWrapper
                key={field}
                field={field}
                label={label}
                unit={unit}
                tooltip={tooltip}
                onChange={(val) => updateNutrient(field, val?.trim() || '0')}
                value={String(nutrientsStore.tempNutrientValues[field as keyof IProductNutrient] ?? 0)}
              />
            ))}
          </div>
        </div>
        <ModalFooter>
          <Button
            className={appStore.settings?.is_pos ? 'hidden' : undefined}
            onClick={applyNutrients}
            title={localeStore.t('ap_item_nutrients_popup_apply_btn')}
            loading={false}
            size="large"
            disabled={!nutrientsStore.isValid}
          />
        </ModalFooter>
      </ModalBackground>
    </CustomModalComponent>
  );
});
